<div class="wrapper-revenue-lost">
  <!-- Page Header -->
  <div class="section-title">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div class="d-flex align-items-center">
        <div class="title">{{ "Revenue Lost Report" | translate }}</div>
      </div>

      <!-- Quick Action Navigation in Header -->
      <div class="header-actions topbar-actions d-flex align-items-center flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-action-ai ui-classic-only"
          data-ai-purpose="generate summary"
          [disabled]="isLoading || isGeneratingAiSummary"
          (click)="generateAiSummary($event)"
          [title]="'Summarize Revenue Lost Report with AI Assistant' | translate"
        >
          <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
          <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
          <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
          </span>
        </button>
        <a
          routerLink="/dashboard/abandoned-carts"
          class="btn btn-action-secondary ui-classic-only"
        >
          <i class="fi fi-rr-shopping-cart-check mr-1"></i>
          {{ "Abandoned Carts" | translate }}
        </a>
      </div>
    </div>
  </div>

  <div class="section-main mt-4">
    <!-- Purpose & Guidance Description Banner -->
    <div class="report-guide-card mb-4">
      <div class="guide-header d-flex align-items-center mb-2">
        <div class="guide-icon mr-2">
          <i class="fi fi-rr-shield-exclamation"></i>
        </div>
        <div>
          <h2 class="guide-heading font-weight-bold mb-0">
            {{ "Report Purpose & Insights" | translate }}
          </h2>
        </div>
        <!-- Glass theme: the report actions sit in this card (the page title is in the top bar) -->
        <div class="guide-actions ui-glass-only d-flex align-items-center flex-wrap gap-2 ml-auto">
          <button
            type="button"
            class="btn btn-action-ai"
            data-ai-purpose="generate summary"
            [disabled]="isLoading || isGeneratingAiSummary"
            (click)="generateAiSummary($event)"
            [title]="'Summarize Revenue Lost Report with AI Assistant' | translate"
          >
            <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
            <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
            <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
              {{ aiTokenCost }}
            </span>
          </button>
          <a
            routerLink="/dashboard/abandoned-carts"
            class="btn btn-action-secondary"
          >
            <i class="fi fi-rr-shopping-cart-check mr-1"></i>
            {{ "Abandoned Carts" | translate }}
          </a>
        </div>
      </div>
      <p class="intro-text mb-3">
        {{ "Identify abandoned checkout funnels, payment processing declines, and potential revenue leakages across all active landing pages to recover lost conversions." | translate }}
      </p>
      <div class="guide-pills d-flex flex-wrap gap-2">
        <div class="guide-pill">
          <div class="pill-badge pill-badge-primary mr-2">
            <i class="fi fi-rr-bullseye"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "Purpose:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Measure lost revenue potential, cart abandonment rates, and failed checkout transactions per landing page." | translate }}</span>
          </div>
        </div>
        <div class="guide-pill">
          <div class="pill-badge pill-badge-success mr-2">
            <i class="fi fi-rr-bulb"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "When to Use:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Use when diagnosing checkout friction, optimizing payment gateway options, and retargeting customers with recovery campaigns." | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">{{ "Retrieving...please wait" | translate }}</p>
    </div>

    <!-- Failed Loading State -->
    <div class="warn-card mb-3" *ngIf="!isLoading && loadFailed">
      <i class="fi fi-rr-exclamation mr-2"></i>
      <span>{{ "We could not load the revenue lost report. Please try again." | translate }}</span>
      <button type="button" class="btn btn-retry ml-auto" (click)="loadReport()">
        {{ "Retry" | translate }}
      </button>
    </div>

    <!-- Loaded Content -->
    <div *ngIf="!isLoading && !loadFailed">
      <!-- AI Performance Summary & Insights Card -->
      <div
        class="ai-summary-card mb-4"
        *ngIf="showAiSummaryCard"
      >
        <div class="ai-summary-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <div class="ai-icon-badge mr-2">
              <i class="fi fi-rr-sparkles"></i>
            </div>
            <div>
              <div class="ai-title font-weight-bold">
                {{ "AI Performance Summary & Insights" | translate }}
              </div>
              <div class="ai-subtitle text-muted">
                {{ "Automated AI analysis based on current lost revenue metrics and cart abandonment trends" | translate }}
              </div>
            </div>
          </div>

          <div class="ai-actions d-flex align-items-center gap-2">
            <button
              *ngIf="!isGeneratingAiSummary && aiSummaryContent && !aiSummaryError"
              type="button"
              class="btn btn-sm btn-ai-action"
              (click)="copyAiSummary()"
              [title]="'Copy to Clipboard' | translate"
            >
              <i class="fi" [ngClass]="isCopied ? 'fi-rr-check' : 'fi-rr-copy'"></i>
              <span class="ml-1">{{ (isCopied ? "Copied!" : "Copy") | translate }}</span>
            </button>
            <button
              *ngIf="!isGeneratingAiSummary"
              type="button"
              class="btn btn-sm btn-ai-action"
              data-ai-purpose="generate summary"
              (click)="generateAiSummary($event)"
              [title]="'Regenerate Summary' | translate"
            >
              <i class="fi fi-rr-refresh"></i>
              <span class="ml-1">{{ "Regenerate" | translate }}</span>
              <span class="ai-coin-token ai-coin-sm" *ngIf="aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
                {{ aiTokenCost }}
              </span>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-ai-close"
              (click)="dismissAiSummary()"
              [attr.aria-label]="'Close' | translate"
            >
              <i class="fi fi-rr-cross"></i>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isGeneratingAiSummary" class="ai-loading-container py-4 text-center">
          <div class="ai-spinner mb-3">
            <i class="fi fi-rr-sparkles fa-spin"></i>
          </div>
          <div class="font-weight-bold text-primary mb-1">
            {{ "Analyzing revenue lost metrics & generating recovery insights..." | translate }}
          </div>
          <div class="text-muted small">
            {{ "Evaluating lost revenue leakages, payment failures, and cart recovery opportunities" | translate }}
          </div>
          <div class="ai-skeleton-box mt-3">
            <div class="skeleton-line full"></div>
            <div class="skeleton-line three-quarters"></div>
            <div class="skeleton-line half"></div>
          </div>
        </div>

        <!-- Error State -->
        <div *ngIf="!isGeneratingAiSummary && aiSummaryError" class="ai-error-container p-3 my-2">
          <div class="d-flex align-items-center text-danger mb-2">
            <i class="fi fi-rr-exclamation mr-2"></i>
            <span class="font-weight-bold">{{ aiSummaryError }}</span>
          </div>
          <button type="button" class="btn btn-sm btn-primary mt-1" data-ai-purpose="generate summary" (click)="generateAiSummary($event)">
            {{ "Try Again" | translate }}
          </button>
        </div>

        <!-- AI Content Display -->
        <div
          *ngIf="!isGeneratingAiSummary && !aiSummaryError && aiSummaryContent"
          class="ai-summary-body mt-3"
        >
          <!-- Structured Insights Cards Grid -->
          <div class="ai-insights-grid" *ngIf="parsedSections && parsedSections.length > 0">
            <div
              *ngFor="let sec of parsedSections"
              class="ai-insight-card"
              [ngClass]="'theme-' + sec.theme"
            >
              <div class="ai-card-header">
                <span class="ai-card-badge">
                  <i class="fi" [ngClass]="sec.icon"></i>
                </span>
                <h4 class="ai-card-title">{{ sec.title | translate }}</h4>
              </div>
              <div class="ai-card-body">
                <div *ngFor="let row of sec.rows" class="ai-row-item">
                  <span
                    *ngIf="sec.theme === 'recommendations' && row.index"
                    class="item-num-badge"
                  >{{ row.index }}</span>
                  <span
                    *ngIf="sec.theme !== 'recommendations' || !row.index"
                    class="item-bullet"
                  ></span>
                  <div class="item-content">
                    <span *ngIf="row.label" class="ai-label">{{ row.label }}:</span>
                    <span *ngIf="row.value" class="ai-val-highlight ml-1">{{ row.value }}</span>
                    <span *ngIf="row.note" class="ai-note ml-1"><ng-container *ngIf="row.value">– </ng-container>{{ row.note }}</span>
                    <span *ngIf="row.text && !row.label" class="ai-text">{{ row.text }}</span>
                    <a
                      *ngIf="row.url"
                      [href]="row.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ai-url-pill ml-1"
                      [attr.title]="row.url"
                    >
                      <i class="fi fi-rr-arrow-square-right mr-1"></i>
                      <span>{{ displayUrl(row.url) }}</span>
                    </a>
                    <span *ngIf="row.url && row.text && row.label" class="ai-url-extra ml-1 text-muted">
                      {{ row.text }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Raw Fallback if no sections were parsed -->
          <div *ngIf="!parsedSections || parsedSections.length === 0" class="ai-fallback-text p-3">
            <div class="small text-muted" style="white-space: pre-wrap;">{{ aiSummaryContent }}</div>
          </div>
        </div>
      </div>
      <!-- Summary KPI Metric Cards (4 Columns) -->
      <div class="row summary-row mb-4">
        <!-- Lost Revenue -->
        <div class="col-12 col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="stat-card stat-card-loss h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Lost Revenue" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #lostRevenueTip="matTooltip"
                  [matTooltip]="STAT_HINTS.lostRevenue | translate"
                  [attr.aria-label]="STAT_HINTS.lostRevenue | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="lostRevenueTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-loss">
                <i class="fi fi-rr-usd-circle"></i>
              </div>
            </div>
            <div class="stat-value mt-2">
              <span class="stat-currency" *ngIf="summaryCurrency">{{ summaryCurrency }}</span>
              {{ formatAmount(summary.lostRevenue) }}
            </div>
            <div class="stat-subtext mt-1">{{ "Potential earnings missed" | translate }}</div>
          </div>
        </div>

        <!-- Abandoned Carts -->
        <div class="col-12 col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="stat-card stat-card-abandoned h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Abandoned Carts" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #abandonedCartsTip="matTooltip"
                  [matTooltip]="STAT_HINTS.abandonedCarts | translate"
                  [attr.aria-label]="STAT_HINTS.abandonedCarts | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="abandonedCartsTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-abandoned">
                <i class="fi fi-rr-shopping-cart-check"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatCount(summary.abandonedCarts) }}</div>
            <div class="stat-subtext mt-1">{{ "Carts left without purchase" | translate }}</div>
          </div>
        </div>

        <!-- Payment Failed -->
        <div class="col-12 col-sm-6 col-lg-3 mb-3 mb-lg-0">
          <div class="stat-card stat-card-failed h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Payment Failed" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #paymentFailedTip="matTooltip"
                  [matTooltip]="STAT_HINTS.paymentFailed | translate"
                  [attr.aria-label]="STAT_HINTS.paymentFailed | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="paymentFailedTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-failed">
                <i class="fi fi-rr-cross-circle"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatCount(summary.paymentFailed) }}</div>
            <div class="stat-subtext mt-1">{{ "Declined or expired payments" | translate }}</div>
          </div>
        </div>

        <!-- Avg Cart Value -->
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="stat-card stat-card-avg h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Avg Cart Value" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #avgCartValueTip="matTooltip"
                  [matTooltip]="STAT_HINTS.avgCartValue | translate"
                  [attr.aria-label]="STAT_HINTS.avgCartValue | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="avgCartValueTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-avg">
                <i class="fi fi-rr-calculator"></i>
              </div>
            </div>
            <div class="stat-value mt-2">
              <span class="stat-currency" *ngIf="summaryCurrency">{{ summaryCurrency }}</span>
              {{ formatAmount(summary.avgCartValue) }}
            </div>
            <div class="stat-subtext mt-1">{{ "Mean abandoned cart amount" | translate }}</div>
          </div>
        </div>
      </div>

      <!-- Currency tabs -->
      <div class="currency-tabs mb-4" *ngIf="currencies.length > 1">
        <button
          type="button"
          class="currency-tab"
          *ngFor="let c of currencies"
          [class.active]="c === selectedCurrency"
          (click)="selectCurrency(c)"
        >
          {{ c }}
        </button>
      </div>

      <!-- Toolbar: using the exact design from order page -->
      <div class="checkout-toolbar mb-4">
        <div class="filter-options">
          <!-- Sort Dropdown styled as btn-filter from order page -->
          <div ngbDropdown class="d-inline-block">
            <button
              class="btn btn-filter"
              id="dropdownRevenueLostSort"
              ngbDropdownToggle
            >
              <i class="fi fi-rr-settings-sliders mr-1"></i>
              <span>{{ "Sort by" | translate }}: {{ selectedSort.display | translate }}</span>
            </button>
            <div ngbDropdownMenu aria-labelledby="dropdownRevenueLostSort" class="custom-dropdown shadow-md">
              <button
                ngbDropdownItem
                *ngFor="let option of SORT_OPTIONS"
                [class.active]="option.value === selectedSort.value"
                (click)="selectSort(option)"
              >
                {{ option.display | translate }}
              </button>
            </div>
          </div>

          <!-- Direct Export Excel Button -->
          <button
            type="button"
            class="btn btn-filter btn-export-excel"
            (click)="downloadExcel()"
            [disabled]="!filteredRows.length || isExporting"
            title="{{ 'Export to Excel' | translate }}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
              <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
              <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
              <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
            </svg>
            <span class="text-[#107C41] font-weight-medium" *ngIf="!isExporting">{{ "Export Excel" | translate }}</span>
            <span class="text-[#107C41] font-weight-medium" *ngIf="isExporting">{{ "Retrieving...please wait" | translate }}</span>
          </button>
        </div>

        <!-- Search Input Box on the right -->
        <div class="search-box">
          <i class="fi fi-rr-search search-icon"></i>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="searchTerm"
            (input)="applyFilters()"
            placeholder="{{ 'Search page, checkout link or external URL' | translate }}..."
          />
          <button
            *ngIf="searchTerm"
            type="button"
            class="btn-clear-search"
            (click)="clearSearch()"
            aria-label="Clear search"
          >
            <i class="fi fi-rr-cross-small"></i>
          </button>
        </div>
      </div>

      <!-- Export Warning -->
      <div class="warn-card mb-3" *ngIf="exportFailed">
        <i class="fi fi-rr-exclamation mr-2"></i>
        <span>{{ "We could not export this report. Please try again." | translate }}</span>
        <button type="button" class="btn btn-retry ml-auto" (click)="downloadExcel()">
          {{ "Retry" | translate }}
        </button>
      </div>

      <!-- Empty states -->
      <div class="empty-state text-center py-5" *ngIf="rows.length === 0">
        <i class="fi fi-rr-shopping-cart empty-icon"></i>
        <p class="mt-3 text-muted">{{ "No revenue lost recorded yet." | translate }}</p>
      </div>

      <div class="empty-state text-center py-5" *ngIf="rows.length > 0 && filteredRows.length === 0">
        <i class="fi fi-rr-search empty-icon"></i>
        <p class="mt-3 text-muted">{{ "No landing page matches your search." | translate }}</p>
      </div>

      <!-- Report rows -->
      <div *ngIf="filteredRows.length > 0">
        <!-- Column headers, desktop only -->
        <div class="row report-head d-none d-lg-flex">
          <div class="col-lg-4">{{ "Landing Page" | translate }}</div>
          <div class="col-lg-1 text-center">{{ "Currency" | translate }}</div>
          <div class="col-lg-2 text-center">{{ "Abandoned Carts" | translate }}</div>
          <div class="col-lg-2 text-center">{{ "Lost Revenue" | translate }}</div>
          <div class="col-lg-2 text-center">{{ "Avg Cart Value" | translate }}</div>
          <div class="col-lg-1 text-center">{{ "Payment Failed" | translate }}</div>
        </div>

        <div class="card report-card mb-3" *ngFor="let row of pagedRows">
          <div class="row card-items align-items-center">
            <!-- Landing Page URLs -->
            <div class="col-12 col-lg-4 landing-cell">
              <p class="property-text d-lg-none">{{ "Landing Page" | translate }}</p>

              <div class="url-group" *ngFor="let group of row.urlGroups">
                <p class="url-group-label">{{ group.label | translate }}</p>

                <a
                  *ngFor="let url of group.urls | slice: 0 : (group.expanded ? group.urls.length : 1)"
                  [href]="url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="value-text landing-link"
                  [title]="url"
                >
                  {{ displayUrl(url) }}
                  <i class="fi fi-rr-arrow-small-right ml-1 external-icon"></i>
                </a>

                <button
                  type="button"
                  class="btn-url-more"
                  *ngIf="group.urls.length > 1"
                  (click)="group.expanded = !group.expanded"
                  [attr.aria-expanded]="group.expanded"
                >
                  <i class="fi" [ngClass]="group.expanded ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down'"></i>
                  <span *ngIf="!group.expanded">{{ "Show more" | translate }}</span>
                  <span *ngIf="group.expanded">{{ "Show less" | translate }}</span>
                </button>
              </div>

              <p class="value-text mb-0 text-muted" *ngIf="!row.urlGroups.length">-</p>
            </div>

            <!-- Currency -->
            <div class="col-6 col-lg-1 text-lg-center">
              <p class="property-text d-lg-none">{{ "Currency" | translate }}</p>
              <p class="value-text mb-0">
                <span class="currency-badge">{{ row.currency || "-" }}</span>
              </p>
            </div>

            <!-- Abandoned Carts -->
            <div class="col-6 col-lg-2 text-lg-center">
              <p class="property-text d-lg-none">{{ "Abandoned Carts" | translate }}</p>
              <p class="value-text font-weight-bold mb-0">{{ formatCount(row.total_abandoned_cart) }}</p>
            </div>

            <!-- Lost Revenue -->
            <div class="col-6 col-lg-2 text-lg-center">
              <p class="property-text d-lg-none">{{ "Lost Revenue" | translate }}</p>
              <p class="value-text loss-text mb-0">{{ formatAmount(row.total_lost_revenue) }}</p>
              <div class="meter-bar loss-bar" [title]="(lossShare(row) | number:'1.0-1') + '% of peak lost revenue'">
                <span [style.width.%]="lossShare(row)"></span>
              </div>
            </div>

            <!-- Avg Cart Value -->
            <div class="col-6 col-lg-2 text-lg-center">
              <p class="property-text d-lg-none">{{ "Avg Cart Value" | translate }}</p>
              <p class="value-text font-weight-bold mb-0">{{ formatAmount(row.avg_cart_value) }}</p>
            </div>

            <!-- Payment Failed -->
            <div class="col-6 col-lg-1 text-lg-center">
              <p class="property-text d-lg-none">{{ "Payment Failed" | translate }}</p>
              <p class="value-text mb-0" [class.warn-text]="row.total_payment_failed > 0" [class.font-weight-bold]="row.total_payment_failed > 0">
                {{ formatCount(row.total_payment_failed) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Pagination Bar -->
        <div class="row px-4 py-3 pagination-row align-items-center" *ngIf="filteredRows.length > pageSize">
          <div class="col-12 col-md-6 d-flex justify-content-center justify-content-md-start mb-2 mb-md-0">
            <ngb-pagination
              [collectionSize]="filteredRows.length"
              [(page)]="currentPage"
              [pageSize]="pageSize"
              aria-label="Revenue lost pagination"
              [ellipses]="false"
              [rotate]="true"
              [maxSize]="5"
            ></ngb-pagination>
          </div>
          <div class="col-12 col-md-6 text-center text-md-right">
            <div class="text-muted font-weight-500">
              {{ "Showing" | translate }} <b>{{ start + 1 }}–{{ end }}</b> {{ "of" | translate }}
              <b>{{ filteredRows.length }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
